import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CubeIcon, GlobeIcon, RobotIcon, UsersIcon } from '@phosphor-icons/react';

import {
  formatCount,
  formatTokens,
  formatUsd,
  type ModelUsage,
  type SessionUsage,
  type TeamUsage,
  type ToolUsage,
  type UserUsage,
} from '@/api-models/usage';
import { Card } from '@/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';
import { TabBar } from '@/ui/Tabs';
import { teamPath } from '@/contexts/space-context';
import { formatTimeAgo } from '@/utils/format';
import { buildSeries, OTHER_COLOR } from './series';

type Dimension = 'members' | 'sessions' | 'models' | 'tools';

/**
 * The same money, cut four ways.
 *
 * This is also the chart's **table view**: three of the light-mode series hues
 * fall under 3:1 against the page surface, which the palette's relief rule allows
 * only when the values are also readable as text. The Members tab carries the
 * same colors as the chart's legend, so that obligation is met here.
 */
export function UsageBreakdown({ usage, teamId }: { usage: TeamUsage; teamId: string }) {
  const [dimension, setDimension] = useState<Dimension>('members');
  const colors = new Map(buildSeries(usage).map((s) => [s.key, s.color]));

  return (
    <Card radius="xl" padding="none" className="overflow-hidden">
      <div className="flex items-center justify-between gap-4 border-b border-line-subtle px-4 py-3">
        <TabBar
          value={dimension}
          onChange={setDimension}
          ariaLabel="Usage breakdown"
          tabs={[
            { value: 'members', label: 'Members', icon: <UsersIcon size={14} weight="duotone" /> },
            { value: 'sessions', label: 'Sessions', icon: <CubeIcon size={14} weight="duotone" /> },
            { value: 'models', label: 'Models', icon: <RobotIcon size={14} weight="duotone" /> },
            { value: 'tools', label: 'Tools', icon: <GlobeIcon size={14} weight="duotone" /> },
          ]}
        />
        {dimension === 'sessions' && usage.by_session.length < usage.session_count && (
          <span className="text-xs text-fg-muted">
            Top {formatCount(usage.by_session.length)} of {formatCount(usage.session_count)}
          </span>
        )}
      </div>

      {dimension === 'members' && <MemberTable rows={usage.by_user} total={usage.total_micro_usd} colors={colors} />}
      {dimension === 'sessions' && (
        <SessionTable rows={usage.by_session} total={usage.total_micro_usd} teamId={teamId} />
      )}
      {dimension === 'models' && <ModelTable rows={usage.by_model} total={usage.total_micro_usd} />}
      {dimension === 'tools' && <ToolTable rows={usage.by_tool} total={usage.total_micro_usd} />}
    </Card>
  );
}

/**
 * Share-of-total bar. Recessive on purpose — it exists to make the ranking
 * scannable, not to compete with the figure beside it.
 */
function ShareBar({ value, total, color }: { value: number; total: number; color?: string }) {
  const pct = total > 0 ? Math.max(0, (value / total) * 100) : 0;
  return (
    <span className="flex items-center gap-2">
      <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-surface-3">
        <span
          className="block h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: color ?? 'var(--color-brand)' }}
        />
      </span>
      <span className="w-9 shrink-0 text-right text-xs tabular-nums text-fg-muted">{pct.toFixed(0)}%</span>
    </span>
  );
}

function Cost({ micro }: { micro: number }) {
  return <span className="font-medium tabular-nums text-fg">{formatUsd(micro)}</span>;
}

function EmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="py-8 text-center text-xs text-fg-tertiary">
        {message}
      </TableCell>
    </TableRow>
  );
}

function MemberTable({ rows, total, colors }: { rows: UserUsage[]; total: number; colors: Map<string, string> }) {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Member</TableHeaderCell>
          <TableHeaderCell className="text-right">Spend</TableHeaderCell>
          <TableHeaderCell>Share</TableHeaderCell>
          <TableHeaderCell className="text-right">Sessions</TableHeaderCell>
          <TableHeaderCell className="text-right">Requests</TableHeaderCell>
          <TableHeaderCell className="text-right">Tokens</TableHeaderCell>
          <TableHeaderCell className="text-right">Last active</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 && <EmptyRow colSpan={7} message="No spend in this period." />}
        {rows.map((u) => (
          <TableRow key={u.user_id ?? 'unattributed'}>
            <TableCell>
              <span className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: (u.user_id && colors.get(u.user_id)) || OTHER_COLOR }}
                />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm text-fg">{u.name ?? 'Unattributed'}</span>
                  {u.email && <span className="truncate text-xs text-fg-muted">{u.email}</span>}
                </span>
              </span>
            </TableCell>
            <TableCell className="text-right">
              <Cost micro={u.total_micro_usd} />
            </TableCell>
            <TableCell>
              <ShareBar
                value={u.total_micro_usd}
                total={total}
                color={(u.user_id && colors.get(u.user_id)) || OTHER_COLOR}
              />
            </TableCell>
            <TableCell className="text-right tabular-nums">{formatCount(u.session_count)}</TableCell>
            <TableCell className="text-right tabular-nums">
              {formatCount(u.request_count + u.tool_request_count)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatTokens(u.prompt_tokens + u.completion_tokens)}
            </TableCell>
            <TableCell className="text-right text-xs text-fg-muted">
              {u.last_activity_at ? formatTimeAgo(u.last_activity_at) : '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SessionTable({ rows, total, teamId }: { rows: SessionUsage[]; total: number; teamId: string }) {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Session</TableHeaderCell>
          <TableHeaderCell>Member</TableHeaderCell>
          <TableHeaderCell className="text-right">Spend</TableHeaderCell>
          <TableHeaderCell>Share</TableHeaderCell>
          <TableHeaderCell className="text-right">Requests</TableHeaderCell>
          <TableHeaderCell className="text-right">Tokens</TableHeaderCell>
          <TableHeaderCell className="text-right">Last active</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 && <EmptyRow colSpan={7} message="No sessions spent in this period." />}
        {rows.map((s) => (
          <TableRow key={s.session_id}>
            <TableCell>
              <Link
                to={teamPath(teamId, `/sessions/${s.session_id}`)}
                className="flex min-w-0 flex-col text-fg no-underline hover:text-brand"
              >
                <span className="truncate text-sm font-medium">{s.name ?? 'Untitled session'}</span>
                <span className="truncate text-xs text-fg-muted">
                  {[s.repository_name, s.model].filter(Boolean).join(' · ') || s.session_id}
                </span>
              </Link>
            </TableCell>
            <TableCell className="text-xs">{s.user_name ?? '—'}</TableCell>
            <TableCell className="text-right">
              <Cost micro={s.total_micro_usd} />
            </TableCell>
            <TableCell>
              <ShareBar value={s.total_micro_usd} total={total} />
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatCount(s.request_count + s.tool_request_count)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatTokens(s.prompt_tokens + s.completion_tokens)}
            </TableCell>
            <TableCell className="text-right text-xs text-fg-muted">
              {s.last_activity_at ? formatTimeAgo(s.last_activity_at) : '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ModelTable({ rows, total }: { rows: ModelUsage[]; total: number }) {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Model</TableHeaderCell>
          <TableHeaderCell className="text-right">Spend</TableHeaderCell>
          <TableHeaderCell>Share</TableHeaderCell>
          <TableHeaderCell className="text-right">Requests</TableHeaderCell>
          <TableHeaderCell className="text-right">Input</TableHeaderCell>
          <TableHeaderCell className="text-right">Cached</TableHeaderCell>
          <TableHeaderCell className="text-right">Output</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 && <EmptyRow colSpan={7} message="No model calls in this period." />}
        {rows.map((m) => (
          <TableRow key={m.model}>
            <TableCell>
              <span className="flex flex-col">
                <span className="text-sm text-fg">{m.display_name}</span>
                <span className="font-mono text-xs text-fg-muted">{m.model}</span>
              </span>
            </TableCell>
            <TableCell className="text-right">
              <Cost micro={m.total_micro_usd} />
            </TableCell>
            <TableCell>
              <ShareBar value={m.total_micro_usd} total={total} />
            </TableCell>
            <TableCell className="text-right tabular-nums">{formatCount(m.request_count)}</TableCell>
            <TableCell className="text-right tabular-nums">{formatTokens(m.prompt_tokens)}</TableCell>
            <TableCell className="text-right tabular-nums">{formatTokens(m.cached_prompt_tokens)}</TableCell>
            <TableCell className="text-right tabular-nums">{formatTokens(m.completion_tokens)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ToolTable({ rows, total }: { rows: ToolUsage[]; total: number }) {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Capability</TableHeaderCell>
          <TableHeaderCell className="text-right">Spend</TableHeaderCell>
          <TableHeaderCell>Share</TableHeaderCell>
          <TableHeaderCell className="text-right">Calls</TableHeaderCell>
          <TableHeaderCell className="text-right">Units</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 && <EmptyRow colSpan={5} message="No metered capabilities used in this period." />}
        {rows.map((t) => (
          <TableRow key={`${t.provider}:${t.operation}`}>
            <TableCell>
              <span className="flex flex-col">
                <span className="text-sm capitalize text-fg">{t.operation.replace(/[_-]/g, ' ')}</span>
                <span className="text-xs text-fg-muted">{t.provider}</span>
              </span>
            </TableCell>
            <TableCell className="text-right">
              <Cost micro={t.total_micro_usd} />
            </TableCell>
            <TableCell>
              <ShareBar value={t.total_micro_usd} total={total} />
            </TableCell>
            <TableCell className="text-right tabular-nums">{formatCount(t.tool_request_count)}</TableCell>
            <TableCell className="text-right tabular-nums">{formatCount(t.units)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
